import { useAuth, useSignIn } from '@clerk/nextjs'
import { Button, Field, Link } from '@components/gassan-ui'
import InlineNotification from '@components/inline-notification'
import { cn } from '@lib/cn'
import { useClerkErrorMessage } from '@lib/hooks/use-clerk-error-message'
import { useSignInRedirectUrl } from '@lib/hooks/use-sign-in-redirect-url'
import { emailRequired } from '@lib/yup'
import { Formik, Form as FormikForm, FormikProps } from 'formik'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import * as Yup from 'yup'
import { ForgotPasswordModal } from '../forgot-password-modal'
import { useNeedsPasswordResetMutation } from '@generated'

const loginSchema = Yup.object().shape({
  email: emailRequired,
  password: Yup.string().required('forms.errors.isRequired'),
})

type FormValues = {
  email: string
  password: string
}

export function LoginForm() {
  const { t } = useTranslation(['forms', 'other', 'navigation'])
  const [error, setError] = useState<string | null>(null)
  const { isLoaded, signIn, setActive } = useSignIn()
  const { isSignedIn, signOut } = useAuth()
  const getClerkErrorMessage = useClerkErrorMessage()
  const [forgotPasswordModalState, setForgotPasswordModalState] = useState<{
    status: 'closed' | 'verify' | 'request'
    copyStyle?: 'forgotten' | 'reset-required'
  }>({ status: 'closed', copyStyle: undefined })
  const signInRedirectUrl = useSignInRedirectUrl()
  const [, needsPasswordReset] = useNeedsPasswordResetMutation()

  const handleForgotPasswordModalClose = () => {
    setForgotPasswordModalState({ ...forgotPasswordModalState, status: 'closed' })
  }

  const handleForgotPasswordModalChangeStatus = (status: 'closed' | 'verify' | 'request') => {
    setForgotPasswordModalState({ ...forgotPasswordModalState, status })
  }

  return (
    <>
      <Formik
        validateOnChange
        validateOnBlur
        validateOnMount={false}
        validationSchema={loginSchema}
        initialValues={{ email: '', password: '' }}
        onSubmit={async (values) => {
          setError(null)
          try {
            if (!isLoaded) return

            if (isSignedIn) {
              await signOut()
            }

            const needsPasswordResetResult = await needsPasswordReset({ email: values.email })

            if (needsPasswordResetResult.data?.needsPasswordReset === true) {
              await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: values.email,
              })

              setForgotPasswordModalState({ copyStyle: 'reset-required', status: 'verify' })
              return
            }

            const result = await signIn.create({
              strategy: 'password',
              identifier: values.email,
              password: values.password,
            })

            if (result.status === 'complete') {
              await setActive({
                session: result.createdSessionId,
                redirectUrl: signInRedirectUrl,
              })
            } else {
              setError(t('errors.unableToSignIn', { ns: 'forms' }))
            }
          } catch (_error: unknown) {
            const clerkMessage = getClerkErrorMessage(_error)
            setError(clerkMessage || t('errors.unableToSignIn', { ns: 'forms' }))
          }
        }}
      >
        {({ isValid, isSubmitting, values }: FormikProps<FormValues>) => {
          return (
            <>
              {!isSubmitting && error && (
                <InlineNotification variant="error" title={error} className="mb-6 md:mb-8" />
              )}
              <FormikForm>
                <Field
                  field="input"
                  name="email"
                  autoComplete="username"
                  id="login-email"
                  label={t('labels.email', { ns: 'forms' })}
                />
                <Field
                  type="password"
                  field="input"
                  autoComplete="current-password"
                  name="password"
                  label={t('labels.password', { ns: 'forms' })}
                />
                <div
                  className={cn(
                    'mt-6 flex flex-wrap items-center justify-center',
                    'sm:flex-nowrap sm:justify-between',
                    'md:mt-8',
                  )}
                >
                  <Button
                    id="login-button"
                    type="submit"
                    variant="dark"
                    status={isSubmitting ? 'loading' : isValid ? 'idle' : 'disabled'}
                    className="mb-4 w-full sm:mb-0 sm:w-auto"
                  >
                    {t('login', { ns: 'other' })}
                  </Button>
                  <Link
                    variant="brown"
                    type="button"
                    as="button"
                    onClick={() => handleForgotPasswordModalChangeStatus('request')}
                  >
                    {t('forgotPassword', { ns: 'other' })}
                  </Link>
                </div>
              </FormikForm>

              <ForgotPasswordModal
                handleRequestClose={() => handleForgotPasswordModalClose()}
                initialEmail={values.email}
                copyStyle={forgotPasswordModalState.copyStyle}
                status={forgotPasswordModalState.status}
                handleChangeStatus={(status) => handleForgotPasswordModalChangeStatus(status)}
              />
            </>
          )
        }}
      </Formik>
    </>
  )
}
